import { tableScoreColors } from "@_common/colors";
import { SiteAuditVendor } from "../Constant/MIS.interface";
import { months } from "../Constant/Util";

export const ScorePillCell = ({ value }: { value: any }) => {
  const num = value != null ? parseFloat(value) : null;
  return (
    <div
      className="score-button text-center"
      style={{ backgroundColor: tableScoreColors(num ?? 0) }}
    >
      {num == null ? "- -" : `${num}%`}
    </div>
  );
};

export const CriticalIssueBadge = ({ value }: { value: any }) => {
  const status = typeof value === "object" ? value?.status : value;
  const legislations = typeof value === "object" ? value?.legislations : [];
  const isClean =
    !status ||
    status.toLowerCase().includes("no critical") ||
    status === "N/A" ||
    status === "-";
  if (isClean) {
    return <span className="small text-muted">No Critical NC</span>;
  }
  return (
    <div className="text-start">
      {legislations?.length > 0 && (
        <div className="" style={{ fontSize: "11px", lineHeight: "1.4" }}>
          <ul className="list-unstyled mb-0">
            {legislations.map((leg: any, idx: number) => (
              <li
                key={idx}
                className="mb-2 border-start ps-2"
                style={{ borderLeft: "2px solid #ed5565" }}
              >
                <div className="fw-bold text-dark">{leg.name?.trim()}</div>
                {leg.legislation_rules?.length > 0 && (
                  <ul className="ms-1 mt-1 list-unstyled text-muted small">
                    {leg.legislation_rules.map((rule: string, rIdx: number) => (
                      <li key={rIdx} className="ps-2">
                        • {rule?.trim()}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const CenterCell = ({ value }: { value: any }) => (
  <div className="text-center">{value ?? 0}</div>
);

export const ScoreBar = ({
  label,
  score,
}: {
  label: string;
  score: number | string | null;
}) => {
  const numeric = score === null ? null : Number(score);
  const width =
    numeric === null || numeric === 0
      ? 100
      : Math.min(Math.max(numeric, 0), 100);

  const display = numeric === null ? 0 : numeric;
  const bgColor =
    numeric === null
      ? "#dc3545"
      : numeric >= 90
        ? "#198754"
        : numeric >= 70
          ? "#ffc107"
          : "#dc3545";
  return (
    <div className="progress mb-2 position-relative" style={{ height: "28px" }}>
      <div
        style={{
          width: `${width}%`,
          height: "100%",
          backgroundColor: bgColor,
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(255,255,255,.15) 0, rgba(255,255,255,.15) 10px, transparent 10px, transparent 20px)",
          transition: "width 0.6s ease",
        }}
      />
      <span
        className="position-absolute w-100 text-center fw-bold"
        style={{ lineHeight: "28px", fontSize: "12px", color: "white" }}
      >
        {label} : {display.toFixed(2)}%
      </span>
    </div>
  );
};

export const VendorCell = ({ row, handleExcel, handleDocs }: any) => {
  const v: SiteAuditVendor = row.original;
  return (
    <div>
      <div className="fw-bold text-decoration-underline mb-1">
        {v.vendor_name}
      </div>
      <ul className="mb-0 list-unstyled">
        <li>
          <span className="me-1">•</span>
          <a
            href={v.pdf_report_url || "#"}
            className="text-primary small"
            onClick={() => handleDocs(v.vendor_id)}
          >
            Download Documents
          </a>
        </li>
        <li>
          <span className="me-1">•</span>
          <a
            href={v.excel_report_url || "#"}
            className="text-primary small"
            onClick={() => handleExcel(v.vendor_id)}
          >
            Excel Report
          </a>
        </li>
      </ul>
    </div>
  );
};

export const statusHeaderStyle = (isHeader: boolean): React.CSSProperties => ({
  backgroundColor: isHeader ? "#9fa2a7" : "transparent",
  fontWeight: isHeader ? 700 : 400,
  // borderRight: isHeader ? "none" : "1px solid #dee2e6",
});

const baseCell = (): React.CSSProperties => ({
  color: "#444",
  padding: "10px 8px",
  height: "100%",
  display: "flex",
  alignItems: "center",
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  fontSize: "11px",
  // borderBottom: "1px solid #dee2e6",
  // borderRight: "1px solid #dee2e6",
});

export const getLocationInnerColumns = (handleExcel: any, handleDocs: any) => [
  {
    Header: "S.No.",
    accessor: "_row_index",
    width: 60,
    Cell: ({ row }: any) => <div className="text-center">{row.index + 1}</div>,
  },
  {
    Header: "Vendor",
    accessor: "vendor_name",
    width: 260,
    Cell: (props: any) => (
      <VendorCell
        {...props}
        handleExcel={handleExcel}
        handleDocs={handleDocs}
      />
    ),
  },
  {
    Header: "Head Count",
    accessor: "headcount",
    width: 150,
    Cell: CenterCell,
  },
  {
    Header: "Critical Score",
    accessor: "critical_score",
    width: 150,
    Cell: ({ value }: any) => <ScorePillCell value={value} />,
  },
  {
    Header: "Full Score",
    accessor: "full_score",
    width: 150,
    Cell: ({ value }: any) => <ScorePillCell value={value} />,
  },
  {
    Header: "Critical Issue",
    accessor: "critical_issue",
    width: 450,
    Cell: ({ value }: any) => <CriticalIssueBadge value={value} />,
  },
];

export const getRegionInnerColumns = (handleSiteClick?: any) => [
  {
    Header: "S.no.",
    accessor: "_row_index",
    width: 60,
    Cell: ({ row }: any) => <div className="text-center">{row.index + 1}</div>,
  },
  {
    Header: "Site Name",
    accessor: "location_name",
    width: 500,
    Cell: ({ row }: any) => {
      const v = row.original;
      return (
        <span
          className="fw-bold text-decoration-underline text-primary"
          style={{ cursor: "pointer" }}
          onClick={() => {
            if (handleSiteClick) {
              handleSiteClick(v);
            }
          }}
        >
          {v.location_name}
        </span>
      );
    },
  },
  {
    Header: "Location",
    accessor: "city_name",
    width: 350,
  },
  {
    Header: "Critical",
    accessor: "critical_score",
    width: 100,
    Cell: ({ value }: any) => <ScorePillCell value={value} />,
  },
  {
    Header: "Full",
    accessor: "full_score",
    width: 100,
    Cell: ({ value }: any) => <ScorePillCell value={value} />,
  },
  {
    Header: "Vendors",
    accessor: "vendor_count",
    width: 90,
    Cell: CenterCell,
  },
  {
    Header: "Audit Date",
    accessor: "audit_date",
    width: 120,
    Cell: ({ value }: any) => {
      if (!value || value === "N/A" || value === "-")
        return <div className="text-center">-</div>;
      return <div className="text-center">{value}</div>;
    },
  },
];

export const getVendorInnerColumns = (year: string | number) => [
  {
    Header: "S.no.",
    accessor: "_row_index",
    width: 55,
    Cell: ({ row }: any) => <div className="text-center">{row.index + 1}</div>,
  },
  {
    Header: "Site Name",
    accessor: "location_name",
    width: 180,
  },
  {
    Header: "City",
    accessor: "city_name",
    width: 120,
  },

  ...(months || []).map((month) => ({
    Header: `${month} ${year}`,
    accessor: `${month.toLowerCase()}`,
    width: 80,
    Cell: ({ row }: any) => {
      const score = row.values?.[`${month.toLowerCase()}`] ?? null;
      const parsedScore =
        score !== null && score !== undefined ? parseFloat(score) : null;
      return (
        <div
          className="score-button"
          style={{
            backgroundColor: tableScoreColors(parsedScore ?? 0),
          }}
        >
          {parsedScore === null ? "--" : `${parsedScore}%`}
        </div>
      );
    },
  })),
];

export const getLineItemWiseInnerColumn = [
  {
    Header: "S.No.",
    accessor: "_sno",
    width: 60,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div
          style={{
            ...baseCell,
            ...statusHeaderStyle(isHeader),
            justifyContent: "center",
          }}
        >
          {isHeader ? "" : r._sno}
        </div>
      );
    },
  },
  {
    Header: "Check Item",
    accessor: "proof_of_compliance",
    width: 150,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
          {isHeader ? r.label : r.proof_of_compliance || "-"}
        </div>
      );
    },
  },
  {
    Header: "Status",
    accessor: "audit_status",
    width: 100,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
          {isHeader ? "" : r.audit_status || "-"}
        </div>
      );
    },
  },
  {
    Header: "Vendor",
    accessor: "vendor_name",
    width: 250,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
          {isHeader ? "" : r.vendor_name || "-"}
        </div>
      );
    },
  },
  {
    Header: "Region",
    accessor: "region_name",
    width: 120,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
          {isHeader ? "" : r.region_name || "-"}
        </div>
      );
    },
  },
  {
    Header: "Location",
    accessor: "city_name",
    width: 120,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
          {isHeader ? "" : r.city_name || "-"}
        </div>
      );
    },
  },
  {
    Header: "Site",
    accessor: "location_name",
    width: 200,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
          {isHeader ? "" : r.location_name || "-"}
        </div>
      );
    },
  },
  {
    Header: "Audit Date",
    accessor: "audit_date",
    width: 100,
    Cell: ({ row }: any) => {
      const r = row.original;
      const isHeader = r._type === "status_header";
      return (
        <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
          {isHeader ? "" : r.audit_date || "-"}
        </div>
      );
    },
  },
  // {
  //   Header: "Auditor",
  //   accessor: "auditor_name",
  //   width: 150,
  //   Cell: ({ row }: any) => {
  //     const r = row.original;
  //     const isHeader = r._type === "status_header";
  //     return (
  //       <div style={{ ...baseCell, ...statusHeaderStyle(isHeader) }}>
  //         {isHeader ? "" : r.auditor_name || "Audit Support - 9289751008"}
  //       </div>
  //     );
  //   },
  // },
];

export const getComplanceScoreCardInnerColumn = [
  {
    Header: "Site ID",
    accessor: "location_code",
    width: 150,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header")
        return (
          <div className="fw-bold text-primary" style={{ fontSize: "16px" }}>
            {r.label}
          </div>
        );
      if (r._type === "column_header")
        return <div className="fw-bold small">Site ID</div>;
      return <div className="small">{value || r.location_code || "-"}</div>;
    },
  },
  {
    Header: "Site Name",
    accessor: "location_name",
    width: 250,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header") return null;
      if (r._type === "column_header")
        return <div className="fw-bold small">Site Name</div>;
      return <div>{value}</div>;
    },
  },
  {
    Header: "Region",
    accessor: "region_name",
    width: 120,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header") return null;
      if (r._type === "column_header")
        return <div className="fw-bold small">Region</div>;
      return <div className="small">{value}</div>;
    },
  },
  {
    Header: "Vendors",
    accessor: "vendor_count",
    width: 80,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header") return null;
      if (r._type === "column_header")
        return <div className="fw-bold small">Vendors</div>;
      return <div className="small text-center">{value}</div>;
    },
  },
  {
    Header: "Total Employees",
    accessor: "headcount",
    width: 130,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header") return null;
      if (r._type === "column_header")
        return (
          <div className="fw-bold small">Total Numberof vendor employees</div>
        );
      return <div className="small text-center">{value}</div>;
    },
  },
  {
    Header: "Absent",
    accessor: "sites_with_no_submissions",
    width: 100,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header") return null;
      if (r._type === "column_header")
        return <div className="fw-bold small">Absent</div>;
      const perc = r.sites_with_no_submissions_percentage;
      return (
        <div className="small text-center">
          {value !== undefined ? `${value}(${perc || 0}%)` : "-"}
        </div>
      );
    },
  },
  {
    Header: "Critical",
    accessor: "critical_score",
    width: 120,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header") return null;
      if (r._type === "column_header")
        return <div className="fw-bold small">Critical Compliance</div>;
      return (
        <div className="small text-center">
          {value !== undefined ? `${value}%` : "-"}
        </div>
      );
    },
  },
  {
    Header: "Full",
    accessor: "full_score",
    width: 120,
    Cell: ({ row, value }: any) => {
      const r = row.original;
      if (r._type === "city_header") return null;
      if (r._type === "column_header")
        return <div className="fw-bold small">Full Compliance</div>;
      return (
        <div className="small text-center">
          {value !== undefined ? `${value}%` : "-"}
        </div>
      );
    },
  },
  // {
  //   Header: "With Absentees",
  //   accessor: "compliance_score_with_absentee",
  //   width: 120,
  //   Cell: ({ row, value }: any) => {
  //     const r = row.original;
  //     if (r._type === "city_header") return null;
  //     if (r._type === "column_header")
  //       return <div className="fw-bold small">with absentees</div>;
  //     return (
  //       <div className="small text-center">
  //         {value !== undefined ? value : "-"}
  //       </div>
  //     );
  //   },
  // },
  // {
  //   Header: "Without Absentees",
  //   accessor: "compliance_score_without_absentee",
  //   width: 130,
  //   Cell: ({ row, value }: any) => {
  //     const r = row.original;
  //     if (r._type === "city_header") return null;
  //     if (r._type === "column_header")
  //       return <div className="fw-bold small">w/out absentees</div>;
  //     return (
  //       <div className="small text-center">
  //         {value !== undefined ? value : "-"}
  //       </div>
  //     );
  //   },
  // },
];
